import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import Table from '../Components/Tables';

import { useAlert } from '../Components/AlertContext';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import {
  PostCallAnalysisApiProvider,
  usePostCallAnalysisApi,
} from '../Services/PostCallAnalysisApiProvider';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

function PostCallAnalysisHub({ canEdit }) {
  const navigate = useNavigate();
  const { organizationId } = useParams();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();

  const { getConfigs, deleteConfig } = usePostCallAnalysisApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [totalConfigs, setTotalConfigs] = useState(0);

  const tableTitle = 'Post-Call Analysis Configurations';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/post-call-analysis/${row.key}`);
  };

  const handleDelete = async (selectedId) => {
    if (!selectedId) return;
    showConfirmation(
      `Are you sure you want to delete this Post-Call Analysis Configuration?`,
      async () => {
        try {
          setLoading(true);
          await deleteConfig(selectedId);
          await fetchConfigs(storedSkip(), storedLimit());
          showAlert('success', 'Configuration deleted!');
        } catch (err) {
          console.error(err);
          showAlert('fail', 'Failed to delete configuration!');
        } finally {
          setLoading(false);
        }
      },
    );
  };

  const fetchConfigs = async (skip, limit) => {
    setLoading(true);
    try {
      const response = await getConfigs(skip, limit);
      console.log('Fetched Post-Call Analysis Configs:', response);
      setConfigs(response.items);
      setTotalConfigs(response.total);
      setError(null);
    } catch (err) {
      console.error('Error fetching configs:', err);
      setError('Failed to load configurations');
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigs(storedSkip(), storedLimit());
  }, []);

  const columns = [
    { name: 'Name', selector: (row) => row.name, sortable: true },
    { name: 'Description', selector: (row) => row.description || '-' },
    { name: 'Status', selector: (row) => (row.is_active ? 'Active' : 'Inactive') },
    { name: 'Channels', selector: (row) => row.channel_ids?.length || 0 },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at) },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at) },
  ];

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={configs}
                selectableRows
                selectableRowsHighlight
                progressPending={loading}
                paginationPerPage={initialPageSize}
                paginationTotalRows={totalConfigs}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchConfigs}
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() =>
                    navigate(`/organizations/${organizationId}/post-call-analysis/new`)
                  }
                >
                  Create New Configuration
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PostCallAnalysisHubWrapper({ canEdit = true }) {
  return (
    <ConfirmationProvider>
      <PostCallAnalysisApiProvider>
        <PostCallAnalysisHub canEdit={canEdit} />
      </PostCallAnalysisApiProvider>
    </ConfirmationProvider>
  );
}

export default PostCallAnalysisHubWrapper;
